const NEWSLETTER_POPUP_STORAGE = 'newsletter-popup';

// How long to hide the popup after it's dismissed (subscribing hides it for good).
const NEWSLETTER_POPUP_DISMISS_DAYS = 7;

// Delay before the popup opens once the page-view threshold is reached.
const NEWSLETTER_POPUP_DELAY_MS = 3000;

class NewsletterPopup extends ModalDialog {
  constructor() {
    super();

    this.states = this.querySelectorAll('[data-popup-state]');
    this.pageViewsThreshold = parseInt(this.dataset.pageViews, 10) || 2;

    this.querySelector('[data-popup-continue]')?.addEventListener('click', () =>
      this.switchState('newsletter'),
    );

    this.querySelector('[data-popup-shop]')?.addEventListener('click', () =>
      this.hide(),
    );

    this.init();
  }

  connectedCallback() {
    // The theme editor re-renders the section on every change, upgrading a
    // fresh instance while the previous one stays detached in <body>. Drop
    // stale duplicates (and their listeners) so the overlays don't stack.
    document.querySelectorAll('newsletter-popup').forEach((el) => {
      if (el !== this && el.id === this.id) {
        el.editorAbort?.abort();
        el.remove();
      }
    });

    super.connectedCallback();
  }

  init() {
    // In the theme editor, don't auto-trigger. Only show the popup while the
    // merchant has this section selected, so it doesn't block other editing.
    if (window.Shopify?.designMode) {
      this.initEditor();
      return;
    }

    // Shopify reloads the page with `customer_posted=true` after a successful
    // newsletter (customer) form submission — reopen straight into completed.
    if (window.location.search.includes('customer_posted=true')) {
      this.setSubscribed();
      this.switchState('completed');
      this.open();
      this.cleanUrl();
      return;
    }

    // Skip while subscribed (forever) or within the dismiss window.
    if (this.isSuppressed()) return;

    if (this.trackPageView() >= this.pageViewsThreshold) {
      setTimeout(() => {
        this.switchState('initial');
        this.open();
      }, NEWSLETTER_POPUP_DELAY_MS);
    }
  }

  initEditor() {
    const sectionId = this.dataset.sectionId;
    this.editorAbort = new AbortController();
    const { signal } = this.editorAbort;

    document.addEventListener(
      'shopify:section:select',
      (event) => {
        if (event.detail.sectionId === sectionId) this.open();
      },
      { signal },
    );

    document.addEventListener(
      'shopify:section:deselect',
      (event) => {
        if (event.detail.sectionId === sectionId) this.hide();
      },
      { signal },
    );
  }

  open() {
    this.show(this);
  }

  hide() {
    // Snooze re-appearance for a while, unless subscribed or in the editor.
    if (!window.Shopify?.designMode && !this.isSubscribed()) this.snooze();
    super.hide();
  }

  switchState(name) {
    this.states.forEach((state) => {
      state.classList.toggle('is-active', state.dataset.popupState === name);
    });
  }

  trackPageView() {
    const key = `${NEWSLETTER_POPUP_STORAGE}:views`;
    const views = (parseInt(sessionStorage.getItem(key), 10) || 0) + 1;
    sessionStorage.setItem(key, views);
    return views;
  }

  isSubscribed() {
    return (
      localStorage.getItem(`${NEWSLETTER_POPUP_STORAGE}:status`) ===
      'subscribed'
    );
  }

  setSubscribed() {
    localStorage.setItem(`${NEWSLETTER_POPUP_STORAGE}:status`, 'subscribed');
    localStorage.removeItem(`${NEWSLETTER_POPUP_STORAGE}:snooze`);
  }

  snooze() {
    const until =
      Date.now() + NEWSLETTER_POPUP_DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(`${NEWSLETTER_POPUP_STORAGE}:snooze`, until);
  }

  isSuppressed() {
    if (this.isSubscribed()) return true;

    const until = parseInt(
      localStorage.getItem(`${NEWSLETTER_POPUP_STORAGE}:snooze`),
      10,
    );

    if (until && Date.now() < until) return true;

    // Window has elapsed — clear it so the popup can show again.
    if (until) localStorage.removeItem(`${NEWSLETTER_POPUP_STORAGE}:snooze`);

    return false;
  }

  cleanUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('customer_posted');
    window.history.replaceState({}, '', url);
  }
}

customElements.define('newsletter-popup', NewsletterPopup);
